import { TheBodyOfAPaidEventDetail } from '../domains/the-paid-event-the-wallet-shows';
import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { EventsService } from '@smatchy/sdk';
import {
  AddDateFilterToFilterPaidEventsAction,
  AddPriceToFilterPaidEventsAction,
  AddSportSelectedToFilterPaidEventsAction,
  AddStatusToFilterPaidEventsAction,
  FailAction,
  FilterPaidEventAction,
  FilterPaidEventDetailAction,
  FilterPaidEventDetailSuccessAction,
  FilterPaidEventSuccessAction,
  PaidEventKpis,
  PaidEventResponse,
  RemoveDateFilterToFilterPaidEventsAction,
  RemovePriceToFilterPaidEventsAction,
  RemoveSportSelectedToFilterPaidEventsAction,
  RemoveStatusToFilterPaidEventsAction,
  ResetFilterPaidEventsAction,
} from './filter-paid-event.action';
import { catchError, of, tap } from 'rxjs';
export const filterPaidEventStateName = 'filterPaidEvent';

export interface PaidEventStateModel {
  paidEvent: PaidEventResponse;
  kpis: PaidEventKpis;
  paidEventDetail: TheBodyOfAPaidEventDetail | null;
  loading: boolean;
  error?: unknown;
  sportsSelected: string[];
  /**
   * Kept as strings on purpose. They come from route parameters and form
   * values, which are strings, and the API declares them as closed lists.
   * what-the-filter-accepts.ts checks them once, where the DTO is built.
   */
  payoutStatuses: string[];
  /*
   * `| null` on the three of them because that is what the defaults and the
   * reset below write: no date filter is null here, not undefined. Said rather
   * than converted, since the value is persisted and null survives a round trip
   * through localStorage where undefined does not.
   */
  dateFilter: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  priceFrom?: number;
  priceTo?: number;
  pricingChanged?: boolean;
}
const defaults: PaidEventStateModel = {
  paidEvent: {
    data: [],
    total: 0,
    page: 0,
    limit: 0,
  },
  kpis: {
    escrow: 0,
    available: 0,
    transferred: 0,
  },
  loading: false,
  paidEventDetail: null,
  sportsSelected: [],
  payoutStatuses: [],
  dateFilter: null,
  fromDate: null,
  toDate: null,
  priceFrom: 0,
  priceTo: 10000,
  pricingChanged: false,
};
@State<PaidEventStateModel>({
  name: filterPaidEventStateName,
  defaults: defaults,
})
@Injectable()
export class FilterPaidEventState {
  constructor(private readonly _events: EventsService) {}
  @Action(FilterPaidEventAction)
  async filter(ctx: StateContext<PaidEventStateModel>, action: FilterPaidEventAction) {
    ctx.patchState({ loading: true, error: null });
    return this._events.getMyPaidEvents(action.filter).pipe(
      tap((res) => {
        ctx.dispatch(new FilterPaidEventSuccessAction(res as PaidEventResponse));
      }),
      catchError((error) => {
        ctx.dispatch(new FailAction(error));
        return of(error);
      }),
    );
  }
  @Action(FilterPaidEventSuccessAction)
  filterSuccess(ctx: StateContext<PaidEventStateModel>, action: FilterPaidEventSuccessAction) {
    return ctx.patchState({
      paidEvent: action.response,
      loading: false,
      error: null,
    });
  }
  @Action(FilterPaidEventDetailAction)
  async getDetail(ctx: StateContext<PaidEventStateModel>, action: FilterPaidEventDetailAction) {
    ctx.patchState({ loading: true, error: null });
    return this._events
      .getPaidEventById({
        id: action.id,
      })
      .pipe(
        tap((res) => {
          ctx.dispatch(
            new FilterPaidEventDetailSuccessAction({
              // The generated type says { error, value }; this route is not
              // wrapped in an Either, so the body is { event }. Asserted here,
              // at the single point where the generated contract is known to
              // be wrong, rather than by typing every reader as any.
              event: res as unknown as TheBodyOfAPaidEventDetail,
            }),
          );
        }),
        catchError((error) => {
          ctx.dispatch(new FailAction(error));
          return of(error);
        }),
      );
  }
  @Action(FilterPaidEventDetailSuccessAction)
  getDetailSuccess(ctx: StateContext<PaidEventStateModel>, action: FilterPaidEventDetailSuccessAction) {
    return ctx.patchState({
      loading: false,
      paidEventDetail: action.response.event,
    });
  }
  @Action(FailAction)
  loadFail(ctx: StateContext<PaidEventStateModel>, action: FailAction) {
    ctx.patchState({
      loading: false,
      error: action.error,
    });
  }
  @Action(ResetFilterPaidEventsAction)
  async reset(ctx: StateContext<PaidEventStateModel>) {
    ctx.patchState({
      ...defaults,
    });
  }

  @Action(AddSportSelectedToFilterPaidEventsAction)
  async addSportSelectedToFilterAction(
    { patchState, getState, dispatch }: StateContext<PaidEventStateModel>,
    { sportId, resetOther }: AddSportSelectedToFilterPaidEventsAction,
  ) {
    if (resetOther) {
      patchState({
        sportsSelected: [sportId],
      });
    } else {
      patchState({
        sportsSelected: [...new Set([...getState().sportsSelected, sportId])],
      });
    }
  }

  @Action(RemoveSportSelectedToFilterPaidEventsAction)
  async removeSportSelectedToFilterAction(
    { patchState, getState }: StateContext<PaidEventStateModel>,
    { sportId }: RemoveSportSelectedToFilterPaidEventsAction,
  ) {
    patchState({
      sportsSelected: [...getState().sportsSelected.filter((s) => s !== sportId)],
    });
  }
  @Action(RemoveDateFilterToFilterPaidEventsAction)
  async removeDateFilterToFilterAction({ patchState }: StateContext<PaidEventStateModel>) {
    patchState({
      dateFilter: null,
      fromDate: null,
      toDate: null,
    });
  }
  @Action(AddDateFilterToFilterPaidEventsAction)
  async addDateFilterToFilterPaidEventsAction(
    { patchState }: StateContext<PaidEventStateModel>,
    { dateFilter, fromDate, toDate }: AddDateFilterToFilterPaidEventsAction,
  ) {
    if (dateFilter === 'CUSTOM') {
      patchState({
        fromDate,
        toDate,
      });
    } else {
      patchState({
        dateFilter,
      });
    }
  }
  @Action(AddStatusToFilterPaidEventsAction)
  async addStatusToFilterAction(
    { patchState, getState }: StateContext<PaidEventStateModel>,
    { payoutStatus }: AddStatusToFilterPaidEventsAction,
  ) {
    patchState({
      payoutStatuses: [...new Set([...getState().payoutStatuses, payoutStatus])],
    });
  }
  @Action(RemoveStatusToFilterPaidEventsAction)
  async removeStatusToFilterAction(
    { patchState, getState }: StateContext<PaidEventStateModel>,
    { payoutStatus }: RemoveStatusToFilterPaidEventsAction,
  ) {
    if (!payoutStatus) {
      patchState({
        payoutStatuses: [],
      });
      return;
    }
    patchState({
      payoutStatuses: [...getState().payoutStatuses.filter((s) => s !== payoutStatus)],
    });
  }
  @Action(AddPriceToFilterPaidEventsAction)
  async addPriceToFilterAction(
    { patchState }: StateContext<PaidEventStateModel>,
    { priceFrom, priceTo }: AddPriceToFilterPaidEventsAction,
  ) {
    patchState({
      priceFrom,
      priceTo,
      pricingChanged: true,
    });
  }
  @Action(RemovePriceToFilterPaidEventsAction)
  async removePriceToFilterAction({ patchState }: StateContext<PaidEventStateModel>) {
    patchState({
      priceFrom: 0,
      priceTo: 10000,
      pricingChanged: false,
    });
  }
}
